// ═══════════════════════════════════════════════════════════════
// BATCH ENTRY MODAL
// Path: src/components/gifts/BatchEntryPanel.jsx
//
// Modal-based batch gift entry with single-line rows and full
// keyboard navigation. Tab between fields, Enter to save row.
//
// Props:
//   batch        — gift_batches row
//   onBatchUpdate — () => void
//   onClose      — () => void
//
// Blueprint: CMM_Batch_Gift_Entry_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Plus,
  Check,
  X,
  Trash2,
  Lock,
  Unlock,
  Printer,
  Search,
  Upload,
  Pencil,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { PAYMENT_METHODS } from "../../constants/giftTypes";
import { ActionButton } from "../ui/ActionButton";
import { ConfirmDialog } from "../ui/Modal";
import { batchService } from "../../services/batchService";
import { BatchReport } from "./BatchReport";
import { SpektrixImportModal } from "../features/SpektrixImportModal";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

// ── Compact input style ─────────────────────────────────────────
const inp = (palette, width) => ({
  padding: "6px 8px",
  borderRadius: 4,
  fontSize: FONT_SIZES.xs,
  fontFamily: FONT_FAMILY,
  border: `1px solid ${palette.inputBorder}`,
  background: palette.inputBg,
  color: palette.inputColor,
  outline: "none",
  width: width || "100%",
  boxSizing: "border-box",
});

// ═══════════════════════════════════════════════════════════════
// BATCH ENTRY MODAL
// ═══════════════════════════════════════════════════════════════

export const BatchEntryPanel = ({ batch, onBatchUpdate, onClose }) => {
  const {
    palette,
    members,
    currentUser,
    activeFunds,
    showMessage,
    chapterSettings,
    isAdmin,
  } = useApp();

  const [batchGifts, setBatchGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Description (notes) inline editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(batch.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  const handleSaveNotes = async () => {
    const next = notesDraft.trim();
    if (next === (batch.notes || "").trim()) {
      setEditingNotes(false);
      return;
    }
    setSavingNotes(true);
    try {
      await batchService.updateBatchNotes(batch.id, next);
      setEditingNotes(false);
      if (onBatchUpdate) onBatchUpdate();
    } catch (err) {
      showMessage("Failed to update description: " + err.message, "error");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCancelNotes = () => {
    setNotesDraft(batch.notes || "");
    setEditingNotes(false);
  };

  // Shared defaults
  const [sharedDate, setSharedDate] = useState(
    batch.batch_date || new Date().toISOString().slice(0, 10)
  );
  const [sharedFundId, setSharedFundId] = useState(activeFunds?.[0]?.id || "");

  const isOpen = batch.status === "open";
  const canEditNotes = isOpen || isAdmin;

  // ── Load batch gifts ──────────────────────────────────────────
  const loadGifts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await batchService.getBatchGifts(batch.id);
      setBatchGifts(data);
    } catch (error) {
      showMessage("Failed to load batch gifts", "error");
    } finally {
      setLoading(false);
    }
  }, [batch.id, showMessage]);

  useEffect(() => {
    loadGifts();
  }, [loadGifts]);

  // ── Computed totals ───────────────────────────────────────────
  const totalAmount = useMemo(
    () => batchGifts.reduce((sum, g) => sum + (g.amount || 0), 0),
    [batchGifts]
  );

  const typeSummary = useMemo(() => {
    const map = {};
    batchGifts.forEach((g) => {
      const key = g.paymentMethod || "other";
      const label =
        PAYMENT_METHODS.find((m) => m.id === key)?.label || key;
      if (!map[key]) map[key] = { label, count: 0, total: 0 };
      map[key].count++;
      map[key].total += g.amount || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [batchGifts]);

  // ── Batch actions ─────────────────────────────────────────────
  const handleCloseBatch = async () => {
    try {
      await batchService.closeBatch(batch.id, currentUser?.id);
      showMessage("Batch closed");
      setConfirmClose(false);
      onBatchUpdate();
    } catch (error) {
      showMessage("Failed to close batch", "error");
    }
  };

  const handleReopenBatch = async () => {
    try {
      await batchService.reopenBatch(batch.id);
      showMessage("Batch reopened");
      onBatchUpdate();
    } catch (error) {
      showMessage("Failed to reopen batch", "error");
    }
  };

  const handleDeleteBatch = async () => {
    try {
      await batchService.deleteBatch(batch.id);
      showMessage("Batch deleted", "warning");
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      showMessage("Failed to delete batch", "error");
    }
  };

  const handleRemoveGift = async (giftId) => {
    try {
      await batchService.removeGiftFromBatch(giftId);
      await loadGifts();
    } catch (error) {
      showMessage("Failed to remove gift", "error");
    }
  };

  const handleGiftAdded = async () => {
    await loadGifts();
  };

  // ── Table header style ────────────────────────────────────────
  const th = {
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: "0.65rem",
    color: palette.text3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `2px solid ${palette.cardBorder}`,
    whiteSpace: "nowrap",
  };

  const td = {
    padding: "5px 8px",
    fontSize: FONT_SIZES.xs,
    borderBottom: `1px solid ${palette.cardBorder}`,
    fontFamily: FONT_FAMILY,
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      {/* Modal backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          style={{
            background: palette.cardBg,
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            width: "100%",
            maxWidth: 960,
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* ── Modal Header ───────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 20px",
              background: palette.accent1,
              flexShrink: 0,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: FONT_SIZES.md,
                  fontWeight: 700,
                  color: "#fff",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {batch.batch_number}
                <span
                  style={{
                    marginLeft: 10,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: isOpen
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(255,255,255,0.15)",
                  }}
                >
                  {isOpen ? "Open" : "Closed"}
                </span>
              </div>
              <div
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: "rgba(255,255,255,0.7)",
                  marginTop: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {editingNotes ? (
                  <>
                    <input
                      type="text"
                      autoFocus
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveNotes();
                        else if (e.key === "Escape") handleCancelNotes();
                      }}
                      disabled={savingNotes}
                      placeholder="Batch description"
                      style={{
                        flex: 1,
                        minWidth: 240,
                        padding: "4px 8px",
                        borderRadius: 4,
                        border: "1px solid rgba(255,255,255,0.35)",
                        background: "rgba(255,255,255,0.12)",
                        color: "#fff",
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        outline: "none",
                      }}
                    />
                    <button
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                      title="Save"
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 4,
                        padding: "2px 6px",
                        color: "#fff",
                        cursor: savingNotes ? "wait" : "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Check size={ICON_SIZES.xs} />
                    </button>
                    <button
                      onClick={handleCancelNotes}
                      disabled={savingNotes}
                      title="Cancel"
                      style={{
                        background: "rgba(255,255,255,0.1)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 4,
                        padding: "2px 6px",
                        color: "#fff",
                        cursor: savingNotes ? "wait" : "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <X size={ICON_SIZES.xs} />
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ fontStyle: batch.notes ? "normal" : "italic", opacity: batch.notes ? 1 : 0.7 }}>
                      {batch.notes || "No description"}
                    </span>
                    {canEditNotes && (
                      <button
                        onClick={() => {
                          setNotesDraft(batch.notes || "");
                          setEditingNotes(true);
                        }}
                        title={
                          isOpen
                            ? "Edit description"
                            : "Edit description (admin)"
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 2,
                          color: "rgba(255,255,255,0.7)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Pencil size={ICON_SIZES.xs} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <ModalBtn
                icon={Printer}
                label="Report"
                onClick={() => setShowReport(true)}
              />
              {isOpen && (
                <ModalBtn
                  icon={Upload}
                  label="Import CSV"
                  onClick={() => setShowImport(true)}
                />
              )}
              {isOpen && (
                <ModalBtn
                  icon={Lock}
                  label="Close"
                  onClick={() => setConfirmClose(true)}
                />
              )}
              {!isOpen && isAdmin && (
                <ModalBtn
                  icon={Unlock}
                  label="Reopen"
                  onClick={handleReopenBatch}
                />
              )}
              {isAdmin && (
                <ModalBtn
                  icon={Trash2}
                  label="Delete"
                  onClick={() => setConfirmDelete(true)}
                />
              )}
              <button
                onClick={onClose}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                <X size={ICON_SIZES.sm} />
              </button>
            </div>
          </div>

          {/* ── Shared defaults bar ────────────────────────────── */}
          {isOpen && (
            <div
              style={{
                display: "flex",
                gap: 12,
                padding: "10px 20px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                background: `${palette.accent1}06`,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <label
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: palette.text3,
                  textTransform: "uppercase",
                }}
              >
                Batch Date:
              </label>
              <input
                type="date"
                value={sharedDate}
                onChange={(e) => setSharedDate(e.target.value)}
                style={{ ...inp(palette, 140) }}
              />
              <label
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: palette.text3,
                  textTransform: "uppercase",
                  marginLeft: 8,
                }}
              >
                Fund:
              </label>
              <select
                value={sharedFundId}
                onChange={(e) => setSharedFundId(e.target.value)}
                style={{ ...inp(palette, 160), cursor: "pointer" }}
              >
                <option value="">(None)</option>
                {(activeFunds || []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── Content area (scrollable) ──────────────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0" }}>
            {/* Entry row */}
            {isOpen && (
              <EntryRow
                members={members}
                palette={palette}
                sharedDate={sharedDate}
                sharedFundId={sharedFundId}
                activeFunds={activeFunds}
                batchId={batch.id}
                userId={currentUser?.id}
                showMessage={showMessage}
                onSaved={handleGiftAdded}
              />
            )}

            {/* Gift table */}
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 30,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZES.xs,
                }}
              >
                Loading...
              </div>
            ) : batchGifts.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 40,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZES.sm,
                }}
              >
                No gifts in this batch yet.{" "}
                {isOpen && "Type a donor name above to start."}
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: FONT_FAMILY,
                }}
              >
                <thead>
                  <tr style={{ background: `${palette.accent1}05` }}>
                    <th style={{ ...th, width: 30 }}>#</th>
                    <th style={th}>Donor</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    <th style={th}>Type</th>
                    <th style={th}>Check #</th>
                    <th style={th}>Fund</th>
                    {isOpen && <th style={{ ...th, width: 30 }} />}
                  </tr>
                </thead>
                <tbody>
                  {batchGifts.map((gift, idx) => (
                    <tr key={gift.id}>
                      <td style={{ ...td, color: palette.text3 }}>{idx + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>
                        {gift.memberName}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontWeight: 600,
                          color: palette.accent1,
                        }}
                      >
                        {formatCurrency(gift.amount)}
                      </td>
                      <td style={{ ...td, color: palette.text2 }}>
                        {PAYMENT_METHODS.find((m) => m.id === gift.paymentMethod)
                          ?.label || gift.paymentMethod || "—"}
                      </td>
                      <td style={{ ...td, color: palette.text3 }}>
                        {gift.checkNumber || "—"}
                      </td>
                      <td style={{ ...td, color: palette.text2 }}>
                        {gift.fundName || "—"}
                      </td>
                      {isOpen && (
                        <td style={{ ...td, padding: "4px" }}>
                          <button
                            onClick={() => handleRemoveGift(gift.id)}
                            title="Remove"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: palette.text3,
                              padding: 2,
                              display: "flex",
                            }}
                          >
                            <Trash2 size={ICON_SIZES.xs} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Footer: Aggregate ───────────────────────────────── */}
          <div
            style={{
              padding: "12px 20px",
              borderTop: `2px solid ${palette.cardBorder}`,
              background: `${palette.accent1}06`,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: typeSummary.length > 0 ? 8 : 0,
              }}
            >
              <span
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  color: palette.text1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {batchGifts.length} gift{batchGifts.length !== 1 ? "s" : ""}
              </span>
              <span
                style={{
                  fontSize: FONT_SIZES.md,
                  fontWeight: 700,
                  color: palette.accent1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {formatCurrency(totalAmount)}
              </span>
            </div>
            {/* Type breakdown */}
            {typeSummary.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                  color: palette.text2,
                }}
              >
                {typeSummary.map((ts) => (
                  <span key={ts.label}>
                    {ts.label}: {ts.count} ({formatCurrency(ts.total)})
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report modal */}
      {showReport && (
        <BatchReport
          batch={batch}
          gifts={batchGifts}
          chapterName={chapterSettings?.chapterLegalName || ""}
          onClose={() => setShowReport(false)}
        />
      )}

      {/* Spektrix CSV Import — gifts attach to this batch */}
      {showImport && (
        <SpektrixImportModal
          batchId={batch.id}
          onComplete={loadGifts}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* Confirm close */}
      {confirmClose && (
        <ConfirmDialog
          title="Close this batch?"
          message={`${batchGifts.length} gift${batchGifts.length !== 1 ? "s" : ""} totaling ${formatCurrency(totalAmount)}.`}
          confirmLabel="Close Batch"
          variant="primary"
          onConfirm={handleCloseBatch}
          onCancel={() => setConfirmClose(false)}
        />
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete this batch?"
          message="Gifts will be kept but unlinked."
          confirmLabel="Delete"
          onConfirm={handleDeleteBatch}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
};

// ── Small header button ─────────────────────────────────────────
const ModalBtn = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "5px 10px",
      borderRadius: 6,
      border: "none",
      background: "rgba(255,255,255,0.2)",
      color: "#fff",
      fontSize: "0.65rem",
      fontWeight: 600,
      fontFamily: FONT_FAMILY,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    <Icon size={ICON_SIZES.xs} />
    {label}
  </button>
);

// ═══════════════════════════════════════════════════════════════
// ENTRY ROW — Single-line input with full keyboard navigation
// Tab: Donor → Amount → Type → Check# → Fund → Enter saves
// ═══════════════════════════════════════════════════════════════

const EntryRow = ({
  members,
  palette,
  sharedDate,
  sharedFundId,
  activeFunds,
  batchId,
  userId,
  showMessage,
  onSaved,
}) => {
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("check");
  const [checkNumber, setCheckNumber] = useState("");
  const [fundId, setFundId] = useState(sharedFundId);
  const [saving, setSaving] = useState(false);
  const [focusedField, setFocusedField] = useState("donor");

  const donorRef = useRef(null);
  const amountRef = useRef(null);
  const typeRef = useRef(null);
  const checkRef = useRef(null);
  const fundRef = useRef(null);

  // Reset fund when shared changes
  useEffect(() => {
    setFundId(sharedFundId);
  }, [sharedFundId]);

  // Focus donor field on mount — slight delay to ensure modal is rendered
  useEffect(() => {
    const timer = setTimeout(() => donorRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, []);

  // Focus ring style for active field
  const focusRing = (fieldName) => ({
    boxShadow: focusedField === fieldName ? `0 0 0 2px ${palette.accent1}50` : "none",
    borderColor: focusedField === fieldName ? palette.accent1 : palette.inputBorder,
    transition: "box-shadow 0.15s, border-color 0.15s",
  });

  // Hint text based on current focus
  const hintText = {
    donor: "Type 2+ letters to search. Arrow keys to navigate, Enter or Tab to select.",
    amount: "Enter the gift amount. Tab to continue, Enter to save.",
    type: "Select payment type. Tab to continue, Enter to save.",
    check: "Enter check number (optional). Tab to continue, Enter to save.",
    fund: "Select fund (optional). Enter to save this gift and start the next.",
  };

  // ── Autocomplete ──────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!memberSearch.trim() || memberSearch.length < 2) return [];
    const query = memberSearch.toLowerCase();
    return members
      .filter(
        (m) =>
          (m.firstName || "").toLowerCase().includes(query) ||
          (m.lastName || "").toLowerCase().includes(query) ||
          (m.organization || "").toLowerCase().includes(query) ||
          `${m.firstName} ${m.lastName}`.toLowerCase().includes(query) ||
          `${m.lastName}, ${m.firstName}`.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [memberSearch, members]);

  const selectMember = (member) => {
    setSelectedMember(member);
    setMemberSearch(
      `${member.lastName}, ${member.firstName}`.trim()
    );
    setShowDropdown(false);
    setHighlightIdx(-1);
    // Auto-advance to amount
    setTimeout(() => amountRef.current?.focus(), 50);
  };

  // ── Keyboard handler for donor field ──────────────────────
  const handleDonorKeyDown = (e) => {
    if (showDropdown && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && highlightIdx >= 0) {
        e.preventDefault();
        selectMember(filtered[highlightIdx]);
      } else if (e.key === "Tab" && highlightIdx >= 0) {
        selectMember(filtered[highlightIdx]);
      }
    }
  };

  // ── Save on Enter from any field ──────────────────────────
  const handleFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  // ── Save row ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedMember) {
      showMessage("Select a donor first", "error");
      donorRef.current?.focus();
      return;
    }
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      showMessage("Enter a valid amount", "error");
      amountRef.current?.focus();
      return;
    }

    setSaving(true);
    try {
      await batchService.addGiftToBatch(
        batchId,
        {
          memberId: selectedMember.id,
          giftType: "cash",
          amount: amountNum,
          giftDate: sharedDate,
          fundId: fundId || null,
          paymentMethod: paymentMethod,
          checkNumber: checkNumber || null,
        },
        userId
      );

      // Reset for next row
      setSelectedMember(null);
      setMemberSearch("");
      setAmount("");
      setCheckNumber("");
      setPaymentMethod("check");
      setHighlightIdx(-1);
      onSaved();

      // Focus back to donor field for next entry
      setTimeout(() => donorRef.current?.focus(), 50);
    } catch (error) {
      showMessage("Failed to add gift: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const s = inp(palette);

  return (
    <div>
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "10px 20px 6px",
        background: `${palette.accent1}08`,
        alignItems: "flex-end",
        position: "relative",
      }}
    >
      {/* Donor search */}
      <div style={{ flex: "2 1 200px", position: "relative" }}>
        <label
          style={{
            fontSize: "0.6rem",
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            display: "block",
            marginBottom: 2,
          }}
        >
          Donor *
        </label>
        <div style={{ position: "relative" }}>
          <Search
            size={ICON_SIZES.xs}
            style={{
              position: "absolute",
              left: 6,
              top: "50%",
              transform: "translateY(-50%)",
              color: palette.text3,
              pointerEvents: "none",
            }}
          />
          <input
            ref={donorRef}
            value={memberSearch}
            onChange={(e) => {
              setMemberSearch(e.target.value);
              setSelectedMember(null);
              setShowDropdown(true);
              setHighlightIdx(0);
            }}
            onFocus={() => {
              setFocusedField("donor");
              if (memberSearch.length >= 2) setShowDropdown(true);
            }}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            onKeyDown={handleDonorKeyDown}
            placeholder="Type donor name..."
            style={{
              ...s,
              paddingLeft: 22,
              background: selectedMember ? "#dcfce7" : palette.inputBg,
              ...focusRing("donor"),
              ...(selectedMember ? { borderColor: "#16a34a" } : {}),
            }}
          />
        </div>
        {/* Dropdown */}
        {showDropdown && filtered.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 6,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              zIndex: 50,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {filtered.map((member, idx) => (
              <div
                key={member.id}
                onMouseDown={() => selectMember(member)}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  fontSize: FONT_SIZES.xs,
                  fontFamily: FONT_FAMILY,
                  borderBottom: `1px solid ${palette.cardBorder}`,
                  color: palette.text1,
                  background:
                    idx === highlightIdx ? `${palette.accent1}15` : "transparent",
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {member.lastName}, {member.firstName}
                </span>
                {member.organization && (
                  <span style={{ color: palette.text3, marginLeft: 6 }}>
                    — {member.organization}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Amount */}
      <div style={{ flex: "0 0 100px" }}>
        <label
          style={{
            fontSize: "0.6rem",
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            display: "block",
            marginBottom: 2,
          }}
        >
          Amount *
        </label>
        <input
          ref={amountRef}
          type="number"
          min="0.01"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onFocus={() => setFocusedField("amount")}
          onKeyDown={handleFieldKeyDown}
          placeholder="0.00"
          style={{ ...s, ...focusRing("amount") }}
        />
      </div>

      {/* Payment Type */}
      <div style={{ flex: "0 0 120px" }}>
        <label
          style={{
            fontSize: "0.6rem",
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            display: "block",
            marginBottom: 2,
          }}
        >
          Type
        </label>
        <select
          ref={typeRef}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          onFocus={() => setFocusedField("type")}
          onKeyDown={handleFieldKeyDown}
          style={{ ...s, cursor: "pointer", ...focusRing("type") }}
        >
          {PAYMENT_METHODS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {/* Check # (only when check selected) */}
      {paymentMethod === "check" && (
        <div style={{ flex: "0 0 80px" }}>
          <label
            style={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: palette.text3,
              textTransform: "uppercase",
              display: "block",
              marginBottom: 2,
            }}
          >
            Check #
          </label>
          <input
            ref={checkRef}
            value={checkNumber}
            onChange={(e) => setCheckNumber(e.target.value)}
            onFocus={() => setFocusedField("check")}
            onKeyDown={handleFieldKeyDown}
            placeholder="#"
            style={{ ...s, ...focusRing("check") }}
          />
        </div>
      )}

      {/* Fund */}
      <div style={{ flex: "0 0 130px" }}>
        <label
          style={{
            fontSize: "0.6rem",
            fontWeight: 700,
            color: palette.text3,
            textTransform: "uppercase",
            display: "block",
            marginBottom: 2,
          }}
        >
          Fund
        </label>
        <select
          ref={fundRef}
          value={fundId}
          onChange={(e) => setFundId(e.target.value)}
          onFocus={() => setFocusedField("fund")}
          onKeyDown={handleFieldKeyDown}
          style={{ ...s, cursor: "pointer", ...focusRing("fund") }}
        >
          <option value="">(None)</option>
          {(activeFunds || []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving || !selectedMember || !amount}
        title="Save row (Enter)"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 30,
          borderRadius: 6,
          border: "none",
          background:
            saving || !selectedMember || !amount
              ? palette.text3
              : "#16a34a",
          color: "#fff",
          cursor:
            saving || !selectedMember || !amount
              ? "not-allowed"
              : "pointer",
          opacity: saving || !selectedMember || !amount ? 0.4 : 1,
          flexShrink: 0,
          alignSelf: "flex-end",
        }}
      >
        <Check size={ICON_SIZES.sm} />
      </button>
    </div>
    {/* Hint bar */}
    <div
      style={{
        padding: "4px 20px 8px",
        background: `${palette.accent1}08`,
        borderBottom: `2px solid ${palette.accent1}30`,
        fontSize: "0.6rem",
        color: palette.text3,
        fontFamily: FONT_FAMILY,
        fontStyle: "italic",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <span>{hintText[focusedField] || ""}</span>
      <span style={{ fontStyle: "normal", fontWeight: 600, color: palette.accent1 }}>
        Tab = next field &nbsp;&middot;&nbsp; Enter = save gift
      </span>
    </div>
    </div>
  );
};
